import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';

import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme';
import { useDispatch, useSelector } from 'react-redux';

import { Switch } from '..';
import {
    getConferenceAccessLevel,
    getParticipantByIds,
    selectAvailableAccessLevels,
    selectIsWaitingRoomEnabled,
} from '../../../pages/call/selectors';
import { AccessLevel } from '../../../types';
import { kickPeer, setConferenceAccessLevel } from '../../features/moderation/slice';
import { i18n } from '../../helpers/i18n';
import { ModalConfirmPure } from '../modal-confirm';
import css from './index.css';
import { ModalKickConfirmProps } from './types';

export const ModalKickConfirmV2: FunctionComponent<ModalKickConfirmProps> = ({ onClose, participantId }) => {
    const dispatch = useDispatch();
    const participantsByIds = useSelector(getParticipantByIds);
    const availableAccessLevels = useSelector(selectAvailableAccessLevels);
    const isWaitingRoomEnabled = useSelector(selectIsWaitingRoomEnabled);
    const currentAccessLevel = useSelector(getConferenceAccessLevel);
    const displayName = participantsByIds[participantId]?.displayName || '';

    const isB2BConference = Boolean(availableAccessLevels?.includes(AccessLevel.ORGANIZATION));

    const [shouldEnableWaitingRoom, setShouldEnableWaitingRoom] = useState(false);

    const handleWaitingRoomToggle = useCallback(() => {
        setShouldEnableWaitingRoom((value) => !value);
    }, []);

    const waitingRoomAccessLevel = useMemo(() => {
        if (!shouldEnableWaitingRoom) {
            return null;
        }

        return isB2BConference ? AccessLevel.ORGANIZATION : AccessLevel.ADMINS;
    }, [isB2BConference, shouldEnableWaitingRoom]);

    const handleConfirm = useCallback(() => {
        onClose();

        dispatch(
            kickPeer({
                peerId: participantId,
                withBan: true,
            })
        );

        if (waitingRoomAccessLevel) {
            dispatch(setConferenceAccessLevel(waitingRoomAccessLevel));
        }
    }, [dispatch, onClose, participantId, waitingRoomAccessLevel]);

    const description = useMemo(() => {
        if (currentAccessLevel !== AccessLevel.PUBLIC) {
            return (
                <Text typography="body-short-md-regular">
                    {i18n('moderation', 'modal-kick-confirm-text-wr-enabled')}
                </Text>
            );
        } else if (!isWaitingRoomEnabled) {
            return null;
        }

        return (
            <>
                <div className={css.spaceBottomV2}>
                    <Text typography="body-short-md-regular">
                        {i18n('moderation', 'modal-kick-confirm-text-wr-disabled')}
                    </Text>
                </div>
                <Switch
                    labelAfter={i18n('moderation', 'modal-kick-confirm-text-open-wr')}
                    checked={shouldEnableWaitingRoom}
                    onChange={handleWaitingRoomToggle}
                    size="md"
                    stretch
                />
            </>
        );
    }, [isWaitingRoomEnabled, currentAccessLevel, handleWaitingRoomToggle, shouldEnableWaitingRoom]);

    return (
        <ScopedTheme theme="dark">
            <ModalConfirmPure
                visible={true}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={handleConfirm}
                title={i18n('moderation', 'modal-kick-confirm-title-v2', { name: displayName })}
                cancelLabel={i18n('moderation', 'modal-kick-confirm-button-cancel-v2')}
                confirmLabel={i18n('moderation', 'modal-kick-confirm-button-confirm')}
            >
                {description}
            </ModalConfirmPure>
        </ScopedTheme>
    );
};
